import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/context/AppContext";

import Index from "./pages/Index.tsx";

const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));

const ClientLayout = lazy(() => import("./layouts/ClientLayout"));
const ClientHome = lazy(() => import("./pages/client/ClientHome"));
const ProductCatalog = lazy(() => import("./pages/client/ProductCatalog"));
const ScanProduct = lazy(() => import("./pages/client/ScanProduct"));
const Cart = lazy(() => import("./pages/client/Cart"));
const ConfirmLocation = lazy(() => import("./pages/client/ConfirmLocation"));
const Checkout = lazy(() => import("./pages/client/Checkout"));
const Success = lazy(() => import("./pages/client/Success"));
const CheckoutReturn = lazy(() => import("./pages/client/CheckoutReturn"));
const History = lazy(() => import("./pages/client/History"));
const Profile = lazy(() => import("./pages/client/Profile"));
const PaymentMethods = lazy(() => import("./pages/client/PaymentMethods"));
const Notifications = lazy(() => import("./pages/client/Notifications"));
const Security = lazy(() => import("./pages/client/Security"));
const EditProfile = lazy(() => import("./pages/client/EditProfile"));
const ClientLogin = lazy(() => import("./pages/client/ClientLogin"));
const ClientSignup = lazy(() => import("./pages/client/ClientSignup"));
const Subscription = lazy(() => import("./pages/client/Subscription"));

const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminGuard = lazy(() => import("./components/AdminGuard"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const ProductsAdmin = lazy(() => import("./pages/admin/ProductsAdmin"));
const Stock = lazy(() => import("./pages/admin/Stock"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const Checklist = lazy(() => import("./pages/admin/Checklist"));
const Settings = lazy(() => import("./pages/admin/Settings"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner position="top-center" />
      <AppProvider>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route path="/cliente" element={<ClientLayout />}>
                <Route index element={<ClientHome />} />
                <Route path="produtos" element={<ProductCatalog />} />
                <Route path="scan-produto" element={<ScanProduct />} />
                <Route path="carrinho" element={<Cart />} />
                <Route path="confirmar-local" element={<ConfirmLocation />} />
                <Route path="checkout/:id" element={<Checkout />} />
                <Route path="sucesso/:id" element={<Success />} />
                <Route path="retorno" element={<CheckoutReturn />} />
                <Route path="historico" element={<History />} />
                <Route path="perfil" element={<Profile />} />
                <Route path="perfil/pagamento" element={<PaymentMethods />} />
                <Route path="perfil/notificacoes" element={<Notifications />} />
                <Route path="perfil/seguranca" element={<Security />} />
                <Route path="perfil/editar" element={<EditProfile />} />
                <Route path="login" element={<ClientLogin />} />
                <Route path="cadastro" element={<ClientSignup />} />
                <Route path="assinatura" element={<Subscription />} />
              </Route>

              <Route path="/admin-login" element={<AdminLogin />} />

              <Route
                path="/admin"
                element={
                  <AdminGuard>
                    <AdminLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="produtos" element={<ProductsAdmin />} />
                <Route path="estoque" element={<Stock />} />
                <Route path="pedidos" element={<Orders />} />
                <Route path="checklist" element={<Checklist />} />
                <Route path="configuracoes" element={<Settings />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AppProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
